import React, { useMemo } from 'react';
import { Badge, BadgeProps, Box, Tooltip, TooltipProps } from '@chakra-ui/react';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';

interface RoundBadgeWithTooltipProps {
  label: string;
  icon: FontAwesomeIconProps['icon'];
  colorScheme?: BadgeProps['colorScheme'];
  size?: BadgeProps['size'];
  variant?: BadgeProps['colorScheme'];
  children?: BadgeProps['children'];
  counter?: number;
  iconProps?: FontAwesomeIconProps;
  badgeProps?: BadgeProps;
  tooltipProps?: TooltipProps;
}

export const RoundBadgeWithTooltip = (props: RoundBadgeWithTooltipProps): React.JSX.Element => {
  const {
    label,
    icon,
    colorScheme,
    size = 'sm',
    variant = 'solid',
    children,
    counter,
    iconProps,
    badgeProps,
    tooltipProps,
  } = props;

  const { px, width } = useMemo(() => {
    switch (size) {
      case 'xs':
        return { px: '2px', width: '16px' };
      case 'md':
        return { px: '4px', width: '20px' };
      case 'lg':
        return { px: '4px', width: '24px' };
      case 'xl':
        return { px: '5px', width: '26px' };
      case 'sm':
      default:
        return { px: '3px', width: '18px' };
    }
  }, [size]);

  return (
    <Tooltip hasArrow placement="top" label={label} {...tooltipProps}>
      <Badge
        colorScheme={colorScheme}
        size={size}
        variant={variant}
        borderRadius={'full'}
        gap={0}
        px={px}
        {...badgeProps}
      >
        <Box as="span" width={width} textAlign={'center'} className="badge-icon">
          <FontAwesomeIcon icon={icon} {...iconProps} />
        </Box>

        {children && (
          <Box as="span" pr={px}>
            {children}
          </Box>
        )}

        {Boolean(counter) && (
          <Box as="span" pr={px} sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {counter}
          </Box>
        )}
      </Badge>
    </Tooltip>
  );
};
